import { ICellRendererParams } from 'ag-grid-community';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { L10nTranslationService } from 'angular-l10n';
import { BehaviorSubject, Observable, of } from 'rxjs';

export interface IconCellRenderParams extends ICellRendererParams {
  orangeBannerConfig?: { show: boolean; tooltip: string };
  purpleBannerConfig?: { show: boolean; tooltip: string };
  orangeBannerPropertyConfig?: { show: boolean; tooltip: string };
  redBannerConfig?: { show: boolean; tooltip: string };
  cssClass?: string;
  isDisabled$?: (context: Record<string, any>) => BehaviorSubject<boolean>;
}

@Component({
  selector: 'miq-icon-cell-renderer',
  styleUrls: ['./icon-cell-renderer.component.scss'],
  template: `
    <!-- Purple and Orange Banners -->
    @if (showPurpleBanner || showOrangeBanner || (entity === 'property' && showOrangePropertyBanner) || showRedBanner) {
      <div class="banner-container">
        @if (showRedBanner) {
          <div class="ag-icon-cell-red-banner" [matTooltip]="tooltipRedBanner"></div>
        }
        @if (showPurpleBanner) {
          <div class="ag-icon-cell-purple-banner" [matTooltip]="tooltipPurpleBanner"></div>
        }
        @if (showOrangeBanner) {
          <div class="ag-icon-cell-orange-banner" [matTooltip]="tooltipOrangeBanner"></div>
        }
        @if (entity === 'property' && !showOrangeBanner && showOrangePropertyBanner) {
          <div class="ag-icon-cell-orange-banner" [matTooltip]="tooltipOrangePropertyBanner | xTranslate"></div>
        }
      </div>
    }

    <!-- Drag Icon -->
    <a #dragIcon>
      <mat-icon
        *ngIf="rowDrag"
        class="drag icon-cell"
        svgIcon="alt-handle-icon"
        [matTooltip]="
          entity ? ('recordsClipboard.move' | xTranslate) + ('recordsClipboard.' + entity | xTranslate) : null
        "
      ></mat-icon>
    </a>

    <!-- Configured Icon -->
    <mat-icon
      *ngIf="showIcon"
      #iconRef
      [class]="cssClass"
      role="img"
      aria-hidden="true"
      [svgIcon]="icon"
      [matTooltip]="toolTipMessage"
      matTooltipClass="icon-cell-tooltip"
      (click)="iconClick($event)"
      [ngClass]="{
        'icon-cell-disabled': isDisabled$ | async,
      }"
    ></mat-icon>
  `,
  standalone: false,
})
export class IconCellRendererComponent implements ICellRendererAngularComp, OnDestroy {
  public icon = '';
  public onIconClick: (data, $event, elementRef) => void;
  private params: ICellRendererParams;
  public showIcon = false;
  public rowDrag = false;
  public toolTipMessage: string;
  public entity: string;
  public showOrangeBanner = null;
  public tooltipOrangeBanner = null;
  public showPurpleBanner = null;
  public tooltipPurpleBanner = null;
  public showOrangePropertyBanner = null;
  public tooltipOrangePropertyBanner = null;
  public showRedBanner = null;
  public tooltipRedBanner = null;
  public cssClass = 'icon-cell';

  isDisabled$: Observable<boolean>;

  @ViewChild('dragIcon', { read: ElementRef, static: true }) dragIcon: ElementRef;
  @ViewChild('iconRef', { read: ElementRef }) iconRef: ElementRef;

  constructor(public translationService: L10nTranslationService) {}

  refresh(params): boolean {
    this.setValue(params);
    return true;
  }

  agInit(params: ICellRendererParams): void {
    this.setValue(params);
  }

  ngAfterViewInit() {
    if (this.rowDrag) {
      this.params.registerRowDragger(this.dragIcon.nativeElement, undefined, undefined, true);
    }
  }

  ngOnDestroy() {
    this.iconRef = null;
  }

  public iconClick($event) {
    if (this.onIconClick) {
      this.onIconClick(this.params, $event, this.iconRef);
      $event.stopPropagation();
    }
  }

  private setValue(params: IconCellRenderParams) {
    this.onIconClick = params['onIconClick'];
    this.params = params;
    this.icon = this._setValue(params, 'icon');
    const showIconValue = this._setValue(params, 'showIcon');
    this.showIcon = showIconValue == null ? !!this.icon : showIconValue;
    const toolTipMessage = this._setValue(params, 'toolTipMessage');
    if (Array.isArray(toolTipMessage)) {
      const translatedMessages = (toolTipMessage || []).map(message => {
        return this.getTooltip(message);
      });
      this.toolTipMessage = translatedMessages.join('\n\n');
    } else if (toolTipMessage) {
      this.toolTipMessage = this.getTooltip(toolTipMessage);
    }
    this.rowDrag = this._setValue(params, 'rowDrag');
    this.entity = this._setValue(params, 'entity');
    this.showOrangeBanner = params?.orangeBannerConfig?.show;
    this.tooltipOrangeBanner = this.getTooltip(params?.orangeBannerConfig?.tooltip);
    this.showPurpleBanner = params?.purpleBannerConfig?.show;
    this.tooltipPurpleBanner = this.getTooltip(params?.purpleBannerConfig?.tooltip);
    this.showOrangePropertyBanner = params?.orangeBannerPropertyConfig?.show;
    this.tooltipOrangePropertyBanner = this.getTooltip(params?.orangeBannerPropertyConfig?.tooltip);
    this.showRedBanner = params?.redBannerConfig?.show;
    this.tooltipRedBanner = this.getTooltip(params?.redBannerConfig?.tooltip);
    if (params?.isDisabled$) {
      this.isDisabled$ =
        params.isDisabled$?.(params?.context) != null ? params.isDisabled$(params?.context) : of(false);
    }
    if (params?.cssClass) {
      this.cssClass = `icon-cell ${params.cssClass}`;
    }
  }

  private _setValue(params: ICellRendererParams, propName: any) {
    const prop = params[propName];
    if (prop == null) {
      return prop;
    }
    return typeof prop === 'function' ? prop(params) : prop;
  }

  private getTooltip(message: string): string {
    return message && this.translationService.has(message) ? this.translationService.translate(message) : message;
  }
}
